import { useCallback, useState } from 'react';

// Owns the per-session receipt log + syncing-route set for the route
// operations dashboard. Exposes a small surface so consumers (the
// container, the polling tick hook) don't reach into raw setState.
//
// Receipt shape: { id, routeName, runId, status, errorMessage }
// `patchReceipt(id, patch)` collapses the historic
// `setReceipts(prev => prev.map(r => r.id === id ? {...r, ...patch} : r))`
// pattern into one named operation.

export default function useReceiptsAndSyncing() {
  const [receipts, setReceipts] = useState([]);
  const [syncingRoutes, setSyncingRoutes] = useState(() => new Set());

  const addReceipt = useCallback((receipt) => {
    setReceipts((prev) => [...prev, receipt]);
  }, []);

  const patchReceipt = useCallback((id, patch) => {
    setReceipts((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  }, []);

  const dismissReceipt = useCallback((id) => {
    setReceipts((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const markRouteSyncing = useCallback((routeName, syncing) => {
    setSyncingRoutes((prev) => {
      const next = new Set(prev);
      if (syncing) next.add(routeName);
      else next.delete(routeName);
      return next;
    });
  }, []);

  const removeFromSyncing = useCallback((routeNames) => {
    if (!routeNames || routeNames.length === 0) return;
    setSyncingRoutes((prev) => {
      const next = new Set(prev);
      routeNames.forEach((name) => next.delete(name));
      return next;
    });
  }, []);

  return {
    receipts,
    syncingRoutes,
    addReceipt,
    patchReceipt,
    dismissReceipt,
    markRouteSyncing,
    removeFromSyncing,
  };
}
